import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../../../components/layout/Layout';
import {
  UserMinus, Search, Clock, CalendarClock, Calendar as CalendarIcon,
  Megaphone,
  Loader2, DollarSign, Star
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { debounce } from 'lodash-es';
import { useError } from '../../../context/ErrorContext';
import PaginationControls from '../../../components/common/PaginationControls';
import ExportExcelButton from '../../../components/common/ExportExcelButton';
import ExportModal, { ExportField } from '../../../components/common/ExportModal';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { TimeAgoDisplay } from '../../../components/common/DateTimeDisplay';
import { clientsApi } from '../../../api/clientsApi';
import ClientContactInfo from '../../../components/common/ClientContactInfo';
import { formatPhoneNumber } from '../../../utils/phoneUtils';
import { isPro as checkIsPro } from '../../../utils/planPermissions';
import { ProLocked } from '../../../components/pro/ProLocked';
// `date-fns/locale/es` NO tiene default export (solo el nombrado `es`): con
// `import es from …` la constante llegaba undefined y registerLocale se quedaba sin locale.
import { es } from 'date-fns/locale/es';
import { RUTA_CAMPANAS, prefillReactivacionDias, prefillReactivacionRango, prefillReactivacionFrecuentes } from '../../analytics/utils/campaignActions';

registerLocale('es', es);

// --- Types, Constants, Helpers ---
interface WhatsAppClient {
  id: string;
  phoneNumber: string;
  visitorName: string | null;
  email: string | null;
  points: number;
  createdAt: Date | string | null;
  lastVisit: Date | null | string;
  countryCode: string;
  gender: string;
  businessId: string;
  birthDay: number | null;
  birthMonth: number | null;
  registrationBranchId?: string;
  registrationBranchName?: string;
}

type InactivityPeriodOption = 'risk' | '1m' | '3m' | '6m' | '1y' | 'b1-3' | 'b3-6' | 'b6-12' | 'custom';

// Segmento de la tarjeta "clientes frecuentes llevan semanas sin venir" del dashboard:
// 1-4 meses sin venir Y saldo >= RISK_MIN_POINTS (mismo criterio que /analytics/dashboard).
const RISK_MIN_POINTS = 20;

const INACTIVITY_PERIODS: { value: InactivityPeriodOption; label: string }[] = [
  { value: 'risk', label: '⭐ Frecuentes en riesgo (1-4 meses, con saldo)' },
  { value: '1m', label: 'Más de 1 mes' },
  { value: '3m', label: 'Más de 3 meses' },
  { value: '6m', label: 'Más de 6 meses' },
  { value: '1y', label: 'Más de 1 año' },
  { value: 'b1-3', label: 'Entre 1 y 3 meses (tibios)' },
  { value: 'b3-6', label: 'Entre 3 y 6 meses' },
  { value: 'b6-12', label: 'Entre 6 y 12 meses' },
  { value: 'custom', label: 'Fecha específica...' }
];

// O5: bandas de inactividad. min/max en días → lastVisit entre (now-max) y (now-min).
const INACTIVITY_BANDS: Record<string, { minDays: number; maxDays: number }> = {
  'risk': { minDays: 30, maxDays: 120 },
  'b1-3': { minDays: 30, maxDays: 90 },
  'b3-6': { minDays: 90, maxDays: 180 },
  'b6-12': { minDays: 180, maxDays: 365 },
};
const daysAgoDate = (days: number): Date => { const d = new Date(); d.setDate(d.getDate() - days); d.setHours(0, 0, 0, 0); return d; };

const ITEMS_PER_PAGE = 20;
const MIN_SEARCH_LENGTH = 2;

// --- HELPERS ---

const calculateCutoffDate = (period: '1m' | '3m' | '6m' | '1y'): Date => {
  const now = new Date(); const cutoffDate = new Date(now);
  switch (period) {
    case '1m': cutoffDate.setMonth(now.getMonth() - 1); break;
    case '3m': cutoffDate.setMonth(now.getMonth() - 3); break;
    case '6m': cutoffDate.setMonth(now.getMonth() - 6); break;
    case '1y': cutoffDate.setFullYear(now.getFullYear() - 1); break;
  }
  cutoffDate.setHours(0, 0, 0, 0); return cutoffDate;
};

// O1: mapea el período del panel a los "días de inactividad" que entiende el tipo de campaña
// 'inactive' (opciones del form: 15/30/45/60/90/120/180/270/365). El custom se ajusta al más cercano.
const INACTIVE_DAYS_OPTIONS = [15, 30, 45, 60, 90, 120, 180, 270, 365];
const snapInactiveDays = (d: number): number =>
  INACTIVE_DAYS_OPTIONS.reduce((a, b) => (Math.abs(b - d) < Math.abs(a - d) ? b : a), INACTIVE_DAYS_OPTIONS[0]);
const periodToInactiveDays = (period: InactivityPeriodOption, customDate: Date | null): number | null => {
  if (period === '1m') return 30;
  if (period === '3m') return 90;
  if (period === '6m') return 180;
  if (period === '1y') return 365;
  if (!customDate) return null;
  const days = Math.floor((Date.now() - customDate.getTime()) / 86400000);
  return snapInactiveDays(Math.max(15, days));
};

const getPeriodLabel = (period: InactivityPeriodOption, date: Date | null): string => {
  if (period === 'custom') { return date ? `Inactivos desde: ${date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}` : 'Selecciona una fecha'; }
  const match = INACTIVITY_PERIODS.find(p => p.value === period);
  return match ? `Inactivos - ${match.label}` : 'Período inválido';
};

const formatDate = (date: Date | null | string): string => {
    if (!date) return 'N/A';
    try {
        let dateObj: Date;
        if (date instanceof Date) { dateObj = date; }
        else if (typeof date === 'string') { dateObj = new Date(date); }
        else if (date && typeof date === 'object' && 'toDate' in date && typeof (date as any).toDate === 'function') { dateObj = (date as any).toDate(); }
        else { return 'Fecha inválida'; }
        if (isNaN(dateObj.getTime())) return 'Fecha inválida';
        return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(dateObj);
    } catch (e) { console.error("Error formatting date:", e, date); return 'Fecha inválida'; }
};

const formatDaysSinceLastVisit = (days: number): string => { 
    if (days === Infinity) return 'Nunca'; 
    if (days === 0) return 'Hoy'; 
    if (days === 1) return 'Ayer'; 
    return `${days} días`; 
};

/** Calcula días desde la última visita */
const calcDaysSinceLastVisit = (lastVisit: Date | null | string): number => {
    if (!lastVisit) return Infinity;
    try {
        const dateObj = typeof lastVisit === 'string' ? new Date(lastVisit) : lastVisit;
        if (isNaN(dateObj.getTime())) return Infinity;
        const oneDay = 86400000;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const lastVisitDate = new Date(dateObj); lastVisitDate.setHours(0, 0, 0, 0);
        return Math.round(Math.abs((today.getTime() - lastVisitDate.getTime()) / oneDay));
    } catch { return Infinity; }
};

/** Formatea género para exportación */
const formatGender = (gender: string | null | undefined): string => {
    if (!gender) return '-';
    if (gender === 'male') return 'Masculino';
    if (gender === 'female') return 'Femenino';
    return gender;
};

/** Formatea cumpleaños (día/mes) para exportación */
const formatBirthdayExport = (day: number | null | undefined, month: number | null | undefined): string => {
    if (day == null || month == null) return '-';
    const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
    return `${day} ${months[month - 1] || month}`;
};

// ========================================================================
// DEFINICIÓN DE CAMPOS EXPORTABLES
// ========================================================================

const buildExportFields = (pointsHeader: string): ExportField[] => [
    { key: 'name',         header: 'Cliente',              label: 'Nombre del cliente',   alwaysIncluded: true,  columnWidth: 30 },
    { key: 'phone',        header: 'Teléfono',             label: 'Teléfono',             alwaysIncluded: true,  columnWidth: 20 },
    { key: 'daysInactive', header: 'Días inactivo',        label: 'Días inactivo',        alwaysIncluded: true,  columnWidth: 14 },
    { key: 'lastVisit',    header: 'Última visita',        label: 'Última visita',        defaultSelected: true, columnWidth: 18 },
    { key: 'points',       header: pointsHeader,           label: pointsHeader,           defaultSelected: true, columnWidth: 15 },
    { key: 'email',        header: 'Email',                label: 'Correo electrónico',   defaultSelected: true, columnWidth: 28 },
    { key: 'gender',       header: 'Género',               label: 'Género',               defaultSelected: true, columnWidth: 14 },
    { key: 'birthday',     header: 'Cumpleaños',           label: 'Cumpleaños',           defaultSelected: true, columnWidth: 14 },
    { key: 'country',      header: 'País',                 label: 'País (código)',         defaultSelected: false, columnWidth: 10 },
    { key: 'branch',       header: 'Sucursal de Registro', label: 'Sucursal de registro', defaultSelected: true, columnWidth: 22 },
];

/** Mapa de extracción: dado un key, retorna el valor de celda para un cliente */
const getFieldValue = (key: string, client: WhatsAppClient, loyaltyMode: string): string => {
    switch (key) {
        case 'name':         return client.visitorName || 'Sin nombre';
        case 'phone':        return formatPhoneNumber(client.countryCode, client.phoneNumber);
        case 'daysInactive': return formatDaysSinceLastVisit(calcDaysSinceLastVisit(client.lastVisit));
        case 'lastVisit':    return client.lastVisit ? formatDate(client.lastVisit) : 'Nunca';
        case 'points':       return loyaltyMode === 'cashback'
                                  ? (client.points ?? 0).toFixed(2)
                                  : Math.floor(client.points ?? 0).toString();
        case 'email':        return client.email || '-';
        case 'gender':       return formatGender(client.gender);
        case 'birthday':     return formatBirthdayExport(client.birthDay, client.birthMonth);
        case 'country':      return client.countryCode || '-';
        case 'branch':       return client.registrationBranchName || '-';
        default:             return '-';
    }
};

// --- COMPONENT ---

const InactiveClientsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { addError, clearErrors } = useError();
  const contentTopRef = useRef<HTMLDivElement>(null);

  // 1. DETECCIÓN DE MODO DE LEALTAD
  const loyaltyMode = currentUser?.loyaltyMode || 'visits';

  const hasProAccess = checkIsPro(currentUser?.subscription);

  const [clients, setClients] = useState<WhatsAppClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pageChanging, setPageChanging] = useState(false);
  const [currentPageNum, setCurrentPageNum] = useState(1);
  const [totalHits, setTotalHits] = useState(0);
  // O4: suma de saldo/puntos "dormidos" del segmento completo (para el insight de cabecera)
  const [totalInactivePoints, setTotalInactivePoints] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // ?segment=risk (tarjeta del dashboard) → aterrizar YA en el segmento de frecuentes en riesgo.
  const [searchParams] = useSearchParams();
  const [selectedPeriod, setSelectedPeriod] = useState<InactivityPeriodOption>(
    searchParams.get('segment') === 'risk' ? 'risk' : '1m'
  );
  const [customDate, setCustomDate] = useState<Date | null>(null);
  // O2: orden — 'recent' (cronológico, default) o 'value' (mayor saldo/puntos primero)
  const [sortBy, setSortBy] = useState<'recent' | 'value'>('recent');

  // Estado del modal de exportación
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  const showPaywall = !hasProAccess;

  // Campos de exportación (memoizados según loyaltyMode)
  const exportFields = React.useMemo(
      () => buildExportFields(loyaltyMode === 'cashback' ? 'Saldo ($)' : 'Puntos'),
      [loyaltyMode]
  );

  // --- Fetch Inactive Clients ---
  const fetchInactiveClients = useCallback(async (
    term: string = "", page: number = 1, period: InactivityPeriodOption = '1m', dateValue: Date | null = null, sortMode: 'recent' | 'value' = 'recent'
  ) => {
    if (!hasProAccess) { setLoading(false); return; }
    if (!currentUser?.businessId) { 
      if (addError) addError("Error de configuración.", undefined, "esConfig"); 
      setClients([]); setTotalHits(0); setLoading(false); return; 
    }
    
    let cutoffDate: Date | null = null;
    let lowerDate: Date | null = null;
    if (period === 'custom') {
      cutoffDate = dateValue ? new Date(dateValue) : null;
    } else if (INACTIVITY_BANDS[period]) {
      const b = INACTIVITY_BANDS[period];
      cutoffDate = daysAgoDate(b.minDays); // lastVisit <= now - minDays
      lowerDate = daysAgoDate(b.maxDays);  // lastVisit >= now - maxDays
    } else {
      cutoffDate = calculateCutoffDate(period as '1m' | '3m' | '6m' | '1y');
    }

    if (!cutoffDate) {
      setClients([]); setTotalHits(0); setLoading(false); setCurrentPageNum(1); return;
    }

    const isPageChange = currentPageNum !== page && !searchLoading;
    if (isPageChange) { setPageChanging(true); } else { setLoading(true); }
    
    if (clearErrors) clearErrors();
    
    try {
      const { clients: mappedClients, total, totalPoints } = await clientsApi.inactiveClients(
        { term, page, size: ITEMS_PER_PAGE, sort: sortMode, before: cutoffDate, after: lowerDate,
          minPoints: period === 'risk' ? RISK_MIN_POINTS : null }
      );

      setTotalHits(total);
      setTotalInactivePoints(totalPoints);
      setCurrentPageNum(page);
      
      if (isPageChange) {
        setTimeout(() => { setClients(mappedClients); setPageChanging(false); }, 200);
      } else {
        setClients(mappedClients); setLoading(false); setSearchLoading(false);
      }
    } catch (error: any) {
      console.error("Error fetching Inactive Clients from ES:", error);
      if (addError) addError(`Error al obtener inactivos: ${error.message || 'Error desconocido'}`, undefined, "esFetchInactive");
      setClients([]); setTotalHits(0); setTotalInactivePoints(0); setCurrentPageNum(1); setPageChanging(false); setSearchLoading(false);
    } finally { 
      setLoading(false);
    }
  }, [addError, clearErrors, currentUser?.businessId, hasProAccess, currentPageNum, searchLoading]);

  useEffect(() => {
    if (contentTopRef.current) {
      contentTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPageNum]);

  const debouncedSetSearchTerm = useMemo(() => debounce((term: string) => {
    if (term.length === 0 || term.length >= MIN_SEARCH_LENGTH) { 
      setSearchTerm(term); setCurrentPageNum(1); 
    }
  }, 500), []);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value; 
    setSearchInput(term);
    if (term.length === 0) { 
      setSearchTerm(''); setCurrentPageNum(1); debouncedSetSearchTerm.cancel(); 
    } else if (term.length >= MIN_SEARCH_LENGTH) { 
      setSearchLoading(true); debouncedSetSearchTerm(term); 
    } else { 
      setSearchTerm(''); debouncedSetSearchTerm.cancel(); 
    }
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(e.target.value as 'recent' | 'value');
    setCurrentPageNum(1);
  };

  const handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPeriod = e.target.value as InactivityPeriodOption; 
    setSelectedPeriod(newPeriod); setSearchInput(''); setSearchTerm(''); setCurrentPageNum(1);
    if (newPeriod !== 'custom') { setCustomDate(null); }
    else if (!customDate) { setClients([]); setTotalHits(0); setLoading(false); }
  };
  
  const handleCustomDateChange = (date: Date | null) => {
    setSearchInput(''); setSearchTerm(''); setCurrentPageNum(1);
    if (date) { 
      const startOfDay = new Date(date); startOfDay.setHours(0, 0, 0, 0); setCustomDate(startOfDay); 
    } else { 
      setCustomDate(null); setClients([]); setTotalHits(0); setLoading(false); 
    }
  };

  useEffect(() => {
    if (currentUser?.businessId && (selectedPeriod !== 'custom' || customDate)) {
      if (searchTerm === '' || searchTerm.length >= MIN_SEARCH_LENGTH) {
        fetchInactiveClients(searchTerm, currentPageNum, selectedPeriod, customDate, sortBy);
      } else { setLoading(false); setSearchLoading(false); }
    } else { if (!loading) { setClients([]); setTotalHits(0); } }
  }, [currentUser?.businessId, searchTerm, currentPageNum, selectedPeriod, customDate, sortBy, fetchInactiveClients]);

  useEffect(() => { return () => { debouncedSetSearchTerm.cancel(); }; }, [debouncedSetSearchTerm]);

  const handleNextPage = useCallback(() => { 
    if (loading || searchLoading || pageChanging) return;
    if (currentPageNum * ITEMS_PER_PAGE < totalHits) {
      setPageChanging(true); setCurrentPageNum(prev => prev + 1);
      setTimeout(() => { setPageChanging(false); }, 500);
    } 
  }, [loading, searchLoading, pageChanging, currentPageNum, totalHits]);
  
  const handlePrevPage = useCallback(() => { 
    if (loading || searchLoading || pageChanging) return;
    if (currentPageNum > 1) {
      setPageChanging(true); setCurrentPageNum(prev => prev - 1);
      setTimeout(() => { setPageChanging(false); }, 500);
    } 
  }, [loading, searchLoading, pageChanging, currentPageNum]);

  const getDaysSinceLastVisit = useCallback((lastVisit: Date | null | string): number => {
    return calcDaysSinceLastVisit(lastVisit);
  }, []);

  // ✅ EXPORTACIÓN CON SELECCIÓN DE CAMPOS
  const exportToExcel = useCallback(async (selectedKeys: string[]) => {
    if (!currentUser?.businessId) return;

    setIsExporting(true);

    try {
      let cutoffDate: Date | null = null;
      let lowerDate: Date | null = null;
      if (selectedPeriod === 'custom') {
        cutoffDate = customDate ? new Date(customDate) : null;
      } else if (INACTIVITY_BANDS[selectedPeriod]) {
        const b = INACTIVITY_BANDS[selectedPeriod];
        cutoffDate = daysAgoDate(b.minDays);
        lowerDate = daysAgoDate(b.maxDays);
      } else {
        cutoffDate = calculateCutoffDate(selectedPeriod as '1m' | '3m' | '6m' | '1y');
      }

      if (!cutoffDate) return;

      const limit = Math.min(5000, totalHits || 5000);

      const { clients: allClients } = await clientsApi.inactiveClients(
        { term: searchTerm, page: 1, size: limit, sort: sortBy, before: cutoffDate, after: lowerDate }
      );

      if (allClients.length === 0) {
          if(addError) addError("No hay datos para exportar.", "warning", "exporting");
          return;
      }

      // Construir headers y anchos dinámicamente según campos seleccionados
      const selectedFields = exportFields.filter(f => selectedKeys.includes(f.key));
      const headers = selectedFields.map(f => f.header);
      const colWidths = selectedFields.map(f => ({ wch: f.columnWidth || 15 }));

      // Generar filas dinámicamente
      const data = allClients.map(client =>
          selectedFields.map(f => getFieldValue(f.key, client, loyaltyMode))
      );

      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]); 
      ws['!cols'] = colWidths;
      const wb = XLSX.utils.book_new(); 
      XLSX.utils.book_append_sheet(wb, ws, 'Clientes Inactivos');
      const date = new Date().toISOString().split('T')[0]; 
      const periodText = selectedPeriod === 'custom' ? customDate ? `desde_${customDate.toISOString().split('T')[0]}` : 'custom' : selectedPeriod; 
      const fileName = `clientes_inactivos_${periodText}_${date}.xlsx`;
      XLSX.writeFile(wb, fileName);

    } catch (error: any) { 
      if (addError) addError("No se pudo generar archivo Excel.", undefined, 'exportInactiveToExcel', error); 
    } finally {
      setIsExporting(false);
      setShowExportModal(false);
    }
  }, [currentUser?.businessId, selectedPeriod, customDate, searchTerm, totalHits, loyaltyMode, sortBy, exportFields, addError]);

  // O1: crear una campaña de reactivación dirigida a ESTE segmento de inactividad (dinámica por
  // filtro 'inactiveDays', se mantiene fresca). Ignora la búsqueda (eso es solo para navegar la lista).
  const handleCreateReactivationCampaign = useCallback(() => {
    // Frecuentes en riesgo (tarjeta del dashboard): banda + saldo mínimo — el MISMO público que la lista.
    if (selectedPeriod === 'risk') {
      const b = INACTIVITY_BANDS['risk'];
      navigate(RUTA_CAMPANAS, { state: { prefill: prefillReactivacionFrecuentes(b.minDays, b.maxDays, RISK_MIN_POINTS) } });
      return;
    }
    // O5: si es una banda → campaña custom con recencyRange (entre min y max días).
    const band = INACTIVITY_BANDS[selectedPeriod];
    if (band) {
      navigate(RUTA_CAMPANAS, { state: { prefill: prefillReactivacionRango(band.minDays, band.maxDays) } });
      return;
    }
    const days = periodToInactiveDays(selectedPeriod, customDate);
    if (!days) {
      if (addError) addError("Selecciona una fecha para crear la campaña.", "warning", "reactivCampaign");
      return;
    }
    navigate(RUTA_CAMPANAS, { state: { prefill: prefillReactivacionDias(days) } });
  }, [selectedPeriod, customDate, navigate, addError]);

  const canGoPrev = currentPageNum > 1;
  const canGoNext = currentPageNum * ITEMS_PER_PAGE < totalHits;
  const currentPeriodLabel = getPeriodLabel(selectedPeriod, customDate);
  const numColumns = 4; 

  // --- Render Paywall ---
  if (showPaywall) {
    return (
      <Layout>
        <div className="space-y-6 px-2 sm:px-0">
          <div className="animate-fade-in-up" style={{animationDelay: '0.1s'}}>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <UserMinus className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-red-600 dark:text-red-400" /> Clientes Inactivos
            </h1>
            <p className="mt-1 text-sm text-content-2">
              Identifica clientes sin visitas para estrategias de reactivación
            </p>
          </div>
          <ProLocked feature="inactive-clients" />
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="space-y-6 px-2 sm:px-0">
        <div ref={contentTopRef} id="content-top"></div>

        {/* Header con título y botón de exportar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-fade-in-up" style={{animationDelay: '0.1s'}}>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center"><UserMinus className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-red-600 dark:text-red-400" /> Clientes Inactivos</h1>
            <p className="mt-1 text-sm text-content-2">Clientes sin visitas desde el período o fecha seleccionada.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto animate-fade-in-up" style={{animationDelay: '0.15s'}}>
            <button
              onClick={handleCreateReactivationCampaign}
              disabled={loading || searchLoading || pageChanging || (selectedPeriod === 'custom' && !customDate)}
              title="Crea una campaña de WhatsApp para recuperar a estos clientes"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Megaphone className="h-4 w-4 mr-2" /> Crear campaña de reactivación
            </button>
            <ExportExcelButton
              onExport={() => setShowExportModal(true)}
              disabled={clients.length === 0 || loading || searchLoading || pageChanging}
            />
          </div>
        </div>

        {/* O4: insight de cabecera — convierte la lista en una llamada a la acción */}
        {!loading && !searchTerm && totalHits > 0 && (selectedPeriod !== 'custom' || customDate) && (
          <div className="bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/30 rounded-lg p-4 flex items-start gap-3 animate-fade-in-up" style={{animationDelay: '0.15s'}}>
            <Megaphone className="h-5 w-5 text-purple-600 dark:text-purple-300 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-content-2">
              Tienes <span className="font-bold text-content">{totalHits.toLocaleString('es-MX')}</span> clientes dormidos
              {loyaltyMode === 'cashback' ? (
                <> con <span className="font-bold text-content">${totalInactivePoints.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> en saldo sin usar.</>
              ) : (
                <> que acumulan <span className="font-bold text-content">{Math.floor(totalInactivePoints).toLocaleString('es-MX')}</span> puntos sin canjear.</>
              )}
              {' '}Recupéralos con una campaña de reactivación.
            </p>
          </div>
        )}
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{animationDelay: '0.15s'}}>
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 items-end">
              <div>
                <label htmlFor="inactivityPeriod" className="block text-sm font-medium text-content-2 mb-1">Inactivo desde</label>
                <select id="inactivityPeriod" value={selectedPeriod} onChange={handlePeriodChange} disabled={loading || searchLoading || pageChanging} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-line focus:outline-none focus:ring-accent focus:border-blue-500 sm:text-sm rounded-md disabled:opacity-50 disabled:bg-canvas">
                  {INACTIVITY_PERIODS.map(period => ( <option key={period.value} value={period.value}>{period.label}</option> ))}
                </select>
              </div>
              <div>
                <label htmlFor="sortBy" className="block text-sm font-medium text-content-2 mb-1">Ordenar por</label>
                <select id="sortBy" value={sortBy} onChange={handleSortChange} disabled={loading || searchLoading || pageChanging} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-line focus:outline-none focus:ring-accent focus:border-blue-500 sm:text-sm rounded-md disabled:opacity-50 disabled:bg-canvas">
                  <option value="recent">Más recientes</option>
                  <option value="value">{loyaltyMode === 'cashback' ? 'Mayor saldo' : 'Más puntos'}</option>
                </select>
              </div>
              <div className={`sm:col-span-1 ${selectedPeriod === 'custom' ? 'block' : 'hidden'}`}>
                <label htmlFor="customDate" className="block text-sm font-medium text-content-2 mb-1">Fecha específica</label>
                <div className="relative mt-1">
                  <DatePicker id="customDate" selected={customDate} onChange={handleCustomDateChange} dateFormat="dd/MM/yyyy" placeholderText="Selecciona fecha" className="focus:ring-accent focus:border-blue-500 block w-full pl-3 pr-10 py-2 sm:text-sm border-line rounded-md disabled:opacity-50 disabled:bg-canvas" disabled={loading || searchLoading || selectedPeriod !== 'custom' || pageChanging} maxDate={new Date()} showPopperArrow={false} locale="es" popperPlacement="bottom-start" customInput={
                      <button type="button" className="w-full text-left focus:ring-accent focus:border-blue-500 block pl-3 pr-10 py-2 sm:text-sm border border-line rounded-md disabled:opacity-50 disabled:bg-canvas relative bg-surface cursor-pointer">
                        {customDate ? customDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Selecciona fecha' }
                        <CalendarIcon className="h-5 w-5 text-content-3 absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                      </button>
                    }
                  />
                </div>
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="search" className="block text-sm font-medium text-content-2 mb-1">Buscar cliente</label>
                <div className="relative mt-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Search className={`h-5 w-5 ${searchLoading ? "text-blue-500 animate-pulse" : "text-content-3"}`} /></div>
                  <input type="text" id="search" className="focus:ring-accent focus:border-blue-500 block w-full pl-10 sm:text-sm border-line rounded-md disabled:opacity-50 disabled:bg-canvas" placeholder={`Nombre o teléfono`} value={searchInput} onChange={handleSearchInputChange} disabled={loading || (selectedPeriod === 'custom' && !customDate) || pageChanging} />
                  {searchInput.length > 0 && searchInput.length < MIN_SEARCH_LENGTH && ( <div className="text-xs text-content-3 mt-1 pl-1">Escribe al menos {MIN_SEARCH_LENGTH} caracteres para buscar</div> )}
                </div>
              </div>
            </div>
            {!loading && (selectedPeriod !== 'custom' || customDate) && (
              <div className="text-sm text-content-2 mt-1 mb-3 px-1 py-1 rounded bg-surface-2 inline-block">Filtro actual: {currentPeriodLabel} {searchTerm && ( <span className="ml-2 bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300 px-2 py-0.5 text-xs rounded-full">Búsqueda: "{searchTerm}"</span> )}</div>
            )}
            <div className="md:hidden mt-4">
              {(loading || searchLoading || pageChanging) && clients.length === 0 ? (
                Array.from({ length: 3 }).map((_, index) => ( <div key={`skeleton-mobile-${index}`} className="mb-3 border rounded-lg p-3 animate-pulse"><div className="flex justify-between"><div className="w-3/4"><div className="h-4 bg-skeleton rounded w-2/3 mb-2"></div><div className="h-3 bg-skeleton rounded w-1/2 mb-2"></div></div><div className="h-5 w-12 bg-skeleton rounded"></div></div><div className="mt-2 pt-2 border-t border-line"><div className="h-3 bg-skeleton rounded w-24"></div></div></div> ))
              ) : !loading && !searchLoading && !pageChanging && clients.length === 0 ? (
                <div className="text-center py-10 text-sm text-content-3"> {selectedPeriod === 'custom' && !customDate ? 'Selecciona una fecha.' : searchTerm ? 'No se encontraron resultados.' : 'No hay clientes inactivos.' } </div>
              ) : (
                clients.map((client, index) => {
                  const daysSinceLastVisit = getDaysSinceLastVisit(client.lastVisit);
                  return (
                    <div key={client.id} className="border rounded-lg p-3 mb-3 hover:bg-surface-2 animate-fade-in-up transition-all duration-150" style={{animationDelay: `${0.3 + index * 0.05}s`}}>
                      <div className="flex justify-between items-start">
                        <div> <ClientContactInfo clientId={client.id} clientName={client.visitorName} phoneNumber={client.phoneNumber} countryCode={client.countryCode} iconSize="sm" /> </div>
                        
                        <div className={`flex flex-col items-end gap-1`}>
                            <div className="text-xs font-medium text-red-600 dark:text-red-400 flex-shrink-0">{formatDaysSinceLastVisit(daysSinceLastVisit)}</div>
                            <div className={`flex items-center px-2 py-0.5 rounded-md ${loyaltyMode === 'cashback' ? 'bg-green-100 dark:bg-green-500/20' : 'bg-yellow-100 dark:bg-yellow-500/20'}`}>
                                {loyaltyMode === 'cashback' ? <DollarSign className="h-3 w-3 text-green-700 dark:text-green-300 mr-0.5"/> : <Star className="h-3 w-3 text-yellow-600 dark:text-yellow-400 mr-0.5"/>}
                                <span className={`text-xs font-medium ${loyaltyMode === 'cashback' ? 'text-green-800 dark:text-green-300' : 'text-yellow-800 dark:text-yellow-300'}`}>
                                    {loyaltyMode === 'cashback' ? (client.points || 0).toFixed(2) : Math.floor(client.points || 0)}
                                </span>
                            </div>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-line flex justify-between items-center text-xs text-content-3">
                        <div className="flex items-center"> <CalendarClock className="h-3 w-3 mr-1"/> <span>{client.lastVisit ? formatDate(client.lastVisit) : 'Nunca visitó'}</span> </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            
            <div className="hidden md:block mt-2">
              <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                  <div className="shadow border-b border-line sm:rounded-lg">
                    <table className="min-w-full divide-y divide-line">
                      <thead className="bg-surface-2">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Cliente</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Días inactivo</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Última visita</th>
                          <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-content-3 uppercase tracking-wider">
                              {loyaltyMode === 'cashback' ? 'Saldo Disponible' : 'Puntos Disponibles'}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-surface divide-y divide-line">
                        {(loading || searchLoading || pageChanging) && clients.length === 0 ? (
                          Array.from({ length: 5 }).map((_, index) => ( <tr key={`skeleton-${index}`} className="animate-pulse"><td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-skeleton rounded w-1/2 mb-2"></div><div className="h-3 bg-skeleton rounded w-1/3"></div></td><td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-skeleton rounded w-20"></div></td><td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-skeleton rounded w-24"></div></td><td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-skeleton rounded w-16 mx-auto"></div></td></tr> ))
                        ) : !loading && !searchLoading && !pageChanging && clients.length === 0 ? (
                          <tr><td colSpan={numColumns} className="px-6 py-12 text-center text-sm text-content-3">{selectedPeriod === 'custom' && !customDate ? 'Selecciona una fecha.' : searchTerm ? 'No se encontraron resultados.' : 'No hay clientes inactivos.' }</td></tr>
                        ) : (
                          clients.map((client, index) => {
                            const daysSinceLastVisit = getDaysSinceLastVisit(client.lastVisit);
                            return (
                              <tr key={client.id} className="hover:bg-surface-2 animate-fade-in-up transition-all duration-150" style={{animationDelay: `${0.35 + index * 0.03}s`}}>
                                <td className="px-6 py-4 whitespace-nowrap"><ClientContactInfo clientId={client.id} clientName={client.visitorName} phoneNumber={client.phoneNumber} countryCode={client.countryCode} /></td>
                                <td className="px-6 py-4 whitespace-nowrap"><div className={`flex items-center text-sm ${daysSinceLastVisit === Infinity ? 'text-content-3' : 'text-red-600 dark:text-red-400'}`}><Clock className="h-4 w-4 mr-2 flex-shrink-0" />{formatDaysSinceLastVisit(daysSinceLastVisit)}</div></td>
                                <td className="px-6 py-4 whitespace-nowrap">{client.lastVisit ? (<div className="flex items-center text-sm text-content-3"><CalendarClock className="h-4 w-4 mr-2 flex-shrink-0" /><div>{formatDate(client.lastVisit)}<div className="text-xs text-content-3">(<TimeAgoDisplay date={client.lastVisit instanceof Date ? client.lastVisit : new Date(client.lastVisit)} />)</div></div></div>) : (<span className="text-sm text-content-3 italic">Nunca</span>)}</td>
                                
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                    <div className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-sm font-medium ${loyaltyMode === 'cashback' ? 'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-300' : 'bg-yellow-50 dark:bg-yellow-500/10 text-yellow-700 dark:text-yellow-300'}`}>
                                            {loyaltyMode === 'cashback' ? (
                                                <>
                                                    <DollarSign className="h-4 w-4 mr-0.5" />
                                                    {(client.points || 0).toFixed(2)}
                                                </>
                                            ) : (
                                                <>
                                                    <Star className="h-4 w-4 mr-1" />
                                                    {Math.floor(client.points || 0)} pts
                                                </>
                                            )}
                                    </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {totalHits > ITEMS_PER_PAGE && ( <PaginationControls currentPage={currentPageNum} isLoading={loading || searchLoading || pageChanging} onPrevPage={handlePrevPage} onNextPage={handleNextPage} canGoPrev={canGoPrev && !pageChanging} canGoNext={canGoNext && !pageChanging} itemsPerPage={ITEMS_PER_PAGE} countOnPage={clients.length} totalItems={totalHits} /> )}
          {!loading && !searchLoading && !pageChanging && clients.length === 0 && totalHits === 0 && (selectedPeriod !== 'custom' || customDate) && !searchTerm && ( <div className="px-4 pb-5 sm:px-6 text-center text-sm text-content-3">No hay clientes inactivos que cumplan los criterios.</div> )}
        </div>
      </div>
      {pageChanging && ( <div className="fixed bottom-4 right-4 bg-surface rounded-full shadow-lg p-3 flex items-center animate-fade-in z-50"><Loader2 className="h-5 w-5 text-blue-500 animate-spin mr-2" /><span className="text-sm font-medium text-content-2">Cambiando página...</span></div> )}

      {/* Modal de selección de campos para exportar */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={exportToExcel}
        fields={exportFields}
        title="Exportar Clientes Inactivos"
        description="Selecciona los campos que deseas incluir en el reporte Excel."
        totalRecords={totalHits}
        isExporting={isExporting}
      />
    </Layout>
  );
};

export default InactiveClientsPage;